import { useEffect, useRef } from "react";
import type { ConfettiShape } from "@/interfaces/redemption";
import { CANNON_STAGGER_MS } from "../../../constants/confetti";
import { launchConfetti } from "./confetti-helpers";
import { playConfettiCues } from "./confettiAudio";

/**
 * Celebratory confetti — the recipient flow's "arrival" moment. Two bottom-corner
 * cannons throw an arcing canopy up and inward that flutters down over the
 * revealed collection. Fires whenever `trigger` increments; rendered once,
 * persistently, at the flow root so it keeps playing after the unboxing layer that
 * requested it unmounts. Honors prefers-reduced-motion.
 *
 * All palette / physics / particle internals live in ./confetti-helpers.
 */
export function Confetti({
  trigger,
  shape = "mixed",
  colors,
  zIndex = 7010,
}: {
  trigger: number;
  /** Particle shape (campaign Branding setting). `mixed` = random per particle. */
  shape?: ConfettiShape;
  /** Flat color scheme. Omitted/empty ⇒ the default GfG brand foil palette. */
  colors?: string[];
  /** Stacking order. Default suits the recipient flow; the campaign-builder
   *  landing preview raises it above the fullscreen preview overlay (z-9999). */
  zIndex?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Seed with the mount-time trigger so remounting (e.g. the builder mounts this only
  // on the Landing tab) never re-fires the value it mounted with — only real increments
  // fire. Recipient mounts with trigger 0, so behavior there is unchanged.
  const lastFired = useRef(trigger);

  useEffect(() => {
    if (trigger <= 0 || trigger === lastFired.current) return;
    lastFired.current = trigger;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    // Sound cues live in confettiAudio (module scope) so the Unwrap gesture can
    // unlock them on iOS via primeConfettiAudio; here we just fire them, staggered
    // like the two cannons (poof leads, soft pop answers CANNON_STAGGER_MS later).
    playConfettiCues(CANNON_STAGGER_MS);
    if (!canvasRef.current) return;
    // Cancel the rAF loop if the flow unmounts mid-burst (or before a re-fire).
    const cancel = launchConfetti(canvasRef.current, shape, colors);
    return cancel;
  }, [trigger, shape, colors]);

  return (
    // Decorative overlay (empty canvas, pointer-events:none) — not announced by AT.
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex,
        pointerEvents: "none",
      }}
    />
  );
}
